import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Grid,
  Image,
  Title,
  Text,
  Group,
  Badge,
  Stack,
  List,
  ThemeIcon,
  Button,
  Paper,
  SimpleGrid,
  Alert,
  AspectRatio,
} from '@mantine/core';
import { Loader } from '../../components/common/Loader';
import { Carousel } from '@mantine/carousel';
import {
  IconCheck,
  IconPackage,
  IconCalendar,
  IconScale,
  IconMapPin,
  IconArrowLeft,
} from '@tabler/icons-react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { InquiryForm } from '../../components/forms/InquiryForm';
import { notifications } from '@mantine/notifications';
import { productService, Product } from '../../services/productService';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

export const ProductDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    if (!slug) return;

    setLoading(true);
    setError(null);

    try {
      const data = await productService.getProductBySlug(slug);
      setProduct(data || null);
    } catch (err) {
      console.error('Error fetching product:', err);
      setError('Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (url?: string) => {
    if (!url) return "https://via.placeholder.com/600x400?text=No+Image";
    if (url.startsWith("http")) return url;
    const normalized = url.startsWith("/") ? url : `/${url}`;
    return `${API_URL}${normalized}`;
  };

  const handleInquirySubmit = (_values: any) => {
    notifications.show({
      title: 'Inquiry Sent',
      message: `Thank you for your interest in ${product?.name}. We'll contact you soon.`,
      color: 'green',
    });
  };

if (loading) {
  return (
    <Container size="xl" py={50}>
      <div style={{ textAlign: 'center' }}>
        <Loader />
      </div>
    </Container>
  );
}

  if (error || !product) {
    return (
      <Container size="xl" py={50}>
        <Alert color="red" title="Error">
          {error || 'Product not found'}
        </Alert>
        <Button
          component={Link}
          to="/products"
          mt="md"
          leftSection={<IconArrowLeft size={16} />}
        >
          Back to Products
        </Button>
      </Container>
    );
  }

  // SEO data derived from product
  const seoTitle = `${product.name} - Thalvora Exports`;
  const seoDescription = product.shortDescription || product.description || `Premium quality ${product.name} exported from India.`;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        {product.images?.[0] && (
          <meta property="og:image" content={getImageUrl(product.images[0])} />
        )}
      </Helmet>

      <Container size="xl">
        <Button
          component={Link}
          to="/products"
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          mb="lg"
        >
          Back to Products
        </Button>

        <Grid gutter={40}>
          {/* Product Images */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            {product.images?.length ? (
              <Carousel 
                withIndicators 
                loop 
                height={450}
                slideSize="100%"
              >
                {product.images.map((img, i) => (
                  <Carousel.Slide key={i}>
                    <AspectRatio ratio={4 / 3}>
                      <Image
                        src={getImageUrl(img)}
                        alt={`${product.name} - Image ${i + 1}`}
                        fit="contain"
                        fallbackSrc="https://via.placeholder.com/600x450?text=No+Image"
                        radius="lg"
                        loading="lazy" 
                      />
                    </AspectRatio>
                  </Carousel.Slide>
                ))}
              </Carousel>
            ) : (
              <AspectRatio ratio={4 / 3}>
                <Image
                  src="https://via.placeholder.com/600x450?text=No+Image"
                  alt={product.name}
                  fit="contain"
                  radius="lg"
                />
              </AspectRatio>
            )}
          </Grid.Col>

          {/* Product Info */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="lg">
              <div>
                <Group justify="space-between" mb="xs">
                  <Title order={1}>{product.name}</Title>
                  <Badge size="lg" color="primary">
                    {product.category?.name}
                  </Badge>
                </Group>
                <Text size="lg" c="dimmed">
                  {product.shortDescription || product.description}
                </Text>
              </div>

              <Paper p="md" withBorder>
                <Stack gap="sm">
                  {product.origin && (
                    <Group>
                      <IconMapPin size={18} />
                      <Text>
                        <strong>Origin:</strong> {product.origin}
                      </Text>
                    </Group>
                  )}
                  {product.moq && (
                    <Group>
                      <IconScale size={18} />
                      <Text>
                        <strong>MOQ:</strong> {product.moq}
                      </Text>
                    </Group>
                  )}
                  {product.shelfLife && (
                    <Group>
                      <IconCalendar size={18} />
                      <Text>
                        <strong>Shelf Life:</strong> {product.shelfLife}
                      </Text>
                    </Group>
                  )}
                  {product.packaging?.length > 0 && (
                    <Group>
                      <IconPackage size={18} />
                      <Text>
                        <strong>Packaging:</strong> {product.packaging.join(', ')}
                      </Text>
                    </Group>
                  )}
                </Stack>
              </Paper>

              {product.description && (
                <div>
                  <Title order={3}>Description</Title>
                  <Text>{product.description}</Text>
                </div>
              )}

              {product.qualityInfo && (
                <div>
                  <Title order={3}>Quality Information</Title>
                  <Text>{product.qualityInfo}</Text>
                </div>
              )}

              {product.specifications && Object.keys(product.specifications).length > 0 && (
                <div>
                  <Title order={3}>Specifications</Title>
                  <SimpleGrid cols={2} spacing="md">
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <Paper key={key} p="sm" withBorder>
                        <Text size="sm" c="dimmed">{key}</Text>
                        <Text fw={600}>{value}</Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </div>
              )}

              <List
                spacing="xs"
                size="sm"
                center
                icon={
                  <ThemeIcon color="primary" size={20} radius="xl">
                    <IconCheck size={12} />
                  </ThemeIcon>
                }
              >
                <List.Item>Premium quality guaranteed</List.Item>
                <List.Item>Export standard packaging</List.Item>
                <List.Item>Timely delivery worldwide</List.Item>
                <List.Item>Competitive pricing</List.Item>
              </List>
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Inquiry Form */}
        <Paper p="xl" withBorder mt={50}>
          <Title order={2} ta="center" mb="xl">
            Inquire About {product.name}
          </Title>
          <InquiryForm
            productName={product.name}
            onSubmit={handleInquirySubmit}
          />
        </Paper>
      </Container>
    </>
  );
};